import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addReport, setActiveReport, setContextPanelTurn } from "../../state/actions";
import { rateTurn } from "../../state/conversationActions";
import { FiAlertTriangle } from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ReportCard from "../ReportCard";
import "./Message.scss";
import Accordion from "../Accordion";
import ChartBlock from "../ChartBlock";
import TableBlock from "../TableBlock";
import SourcesPill from "../SourcesPill";
import MessageActions from "../MessageActions";
import QueryContextBar from "../QueryContextBar";
import ContextPanel from "../ContextPanel";
import VisualBlock from "../VisualBlock";
import ReplyContext, { brandOf } from "../VisualBlock/ReplyContext";
import { copyToClipboard } from "../../utils/copyToClipboard";
import { tidyNarrative } from "../../utils/tidyNarrative";
import { extractNextSteps } from "../../utils/nextSteps";
import { rehypeHighlightDeltas } from "../../utils/highlightDeltas";
import { AI_ASSISTANT_PENDO_EVENTS, trackAssistantEvent } from "../../utils/assistantAnalytics";

// The agent writes tables in its narrative, which base Markdown does not
// support — without this they arrive as pipe characters.
const REMARK_PLUGINS = [remarkGfm];
// "(▲ +1.3 pts, z = 2.4)" becomes a coloured "▲ 1.3 pts · significant".
const REHYPE_PLUGINS = [rehypeHighlightDeltas];

// Markdown wraps a fenced block in <pre><code>. Replacing the <code> alone
// left every widget inside a code shell — a monospace font, `white-space: pre`
// and a horizontal scrollbar, so card text ran off the side instead of
// wrapping. `CodeBlock` renders its own <pre>, so actual code keeps its
// styling and widgets get none.
const Unwrapped = ({ children }) => <>{children}</>;

// A wide table scrolls in its own box instead of pushing the bubble out of
// the panel.
const ScrollingTable = ({ children }) => (
  <div className="md-table">
    <table>{children}</table>
  </div>
);

const TrackedLink = ({ node, ...props }) => (
  <a
    target="_blank"
    rel="noopener noreferrer"
    {...props}
    onClick={(event) => {
      props.onClick?.(event);
      trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.LINK_OPENED, {
        linkType: "message_markdown",
      });
    }}
  />
);

// Defined once, outside the component. Written inline, each render made new
// component types, so React threw away every widget and built it again — a
// chart replayed its entry animation on every streamed chunk.
const MARKDOWN_COMPONENTS = {
  // Fenced blocks are widgets, not code: the agent writes ```highcharts,
  // ```brand-kpi-grid and so on. Anything this build has no renderer for
  // falls back to showing the code.
  code: VisualBlock,
  pre: Unwrapped,
  table: ScrollingTable,
  a: TrackedLink,
};

const Message = ({ msg, previousType, scrollToBottom, messageIndex = 0 }) => {
  const userDetail = useSelector((state) => state.userDetail);
  const userInitial = userDetail?.firstName?.charAt(0).toUpperCase() || "U";
  const activeConversationId = useSelector(
    (state) => state?.chat?.activeConversationId ?? null,
  );
  // Which turn's panel is open lives in the store, not here: it covers the
  // whole transcript, so only one may be open, and the header has to be able
  // to close it when it opens something else over the same space.
  const showContext = useSelector(
    (state) => state?.chat?.contextPanelTurnId === msg.turn_id && !!msg.turn_id,
  );
  const senderType = msg.type || "You";
  const payload = { ...(msg.payload || {}) };
  const fallbackFields = [
    "text",
    "assumption",
    "link",
    "title",
    "description",
    "sources",
    "source_type",
    "grounding_sources",
    "chartConfig",
    "tableConfig",
    "report_id",
    "report_data",
    "report_status",
    "report_status_message",
    "report_title",
  ];

  fallbackFields.forEach((field) => {
    if (msg[field] !== undefined && payload[field] === undefined) {
      payload[field] = msg[field];
    }
  });

      const {
    text = "",
    assumption,
    link,
    title,
    description,
    sources = [],
    source_type,
    grounding_sources = [],
    chartConfig = {},
    tableConfig = { columns: [], rows: [] },
    report_id,
    report_data,
    report_status,
    report_status_message,
    report_title,
    query_params_data,
  } = payload;
  const message_type = msg.message_type || "text";
  const textContent = text || "";
  const assumptionText = assumption || textContent;
  const messageRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const messageElement = messageRef.current;
    if (messageElement) {
      setTimeout(() => {
        messageElement.classList.add("show");
      }, 100);
    }
  }, []);

  // The agent writes Markdown by contract, so its replies are always parsed.
  // Sniffing the text for Markdown characters missed everything whose syntax
  // is line-based — tables, headings, lists and quotes all rendered raw,
  // because none of them contain an asterisk, bracket or backtick. What the
  // user typed is not Markdown and is still shown exactly as they wrote it.

  const isAssistant = senderType !== "You";
  // Stopped is not failed: the user asked for it, so it is a plain note rather
  // than an error. Shown whether or not anything had been written - a stop
  // pressed before the first word still needs to say what happened.
  const wasStopped = msg.turn_status === "stopped";
  const sendFailed = msg.send_failed === true;
  // Nothing to copy and nothing to judge. The actions row on an empty reply
  // offers a thumbs-up on a message that does not exist.
  const hasContent = textContent.length > 0;
  // The closing "Next steps" list is lifted out of the reply and not drawn
  // here: follow-ups belong to the latest reply only, so they are offered once,
  // as the chips above the message box (see ChatWindow), not inside every
  // answer in the conversation.
  const { body: narrative } = isAssistant
    ? extractNextSteps(tidyNarrative(textContent))
    : { body: textContent, suggestions: [] };
  // Cards that compare a number without naming whose it is borrow the brand
  // from the reply's KPI grid.
  const replyBrand = isAssistant ? brandOf(textContent) : undefined;
  const reply = useMemo(() => ({ brand: replyBrand }), [replyBrand]);

  const renderTextMessage = () => (
    <div className={senderType}>
      <div className="markupdiv">
        {senderType === "You" && (
          <div className="username-letter">
            <span>{userInitial}</span>
          </div>
        )}
        <div className="message-content">
          {isAssistant ? (
            <ReplyContext.Provider value={reply}>
            <ReactMarkdown
              remarkPlugins={REMARK_PLUGINS}
              rehypePlugins={REHYPE_PLUGINS}
              components={MARKDOWN_COMPONENTS}
            >
              {narrative}
            </ReactMarkdown>
            </ReplyContext.Provider>
          ) : (
            textContent
          )}
          {query_params_data && (
            <div className="query-params-inline">
              <QueryContextBar data={query_params_data} />
            </div>
          )}
          {sources && sources.length > 0 && message_type !== "sources" && (
            <SourcesPill sources={sources} />
          )}
        </div>
        {wasStopped && (
          <div className="message-stopped" role="note">
            Stopped
          </div>
        )}
        {sendFailed && (
          <div className="message-unsent" role="alert">
            <FiAlertTriangle size={13} aria-hidden="true" />
            <span>Not sent — this message never reached the server.</span>
          </div>
        )}
        {isAssistant && hasContent && (
          <div className="message-actions-outer">
            <MessageActions
              copyText={textContent}
              rating={msg.feedback?.rating ?? null}
              onRate={
                msg.turn_id && activeConversationId
                  ? (value) =>
                      dispatch(
                        rateTurn({
                          conversationId: activeConversationId,
                          turnId: msg.turn_id,
                          value,
                        }),
                      )
                  : undefined
              }
              onCopy={copyToClipboard}
              onShowContext={
                msg.turn_id && activeConversationId
                  ? () => {
                      trackAssistantEvent(
                        AI_ASSISTANT_PENDO_EVENTS.CONTEXT_VIEWED,
                        { turn_id: msg.turn_id },
                      );
                      dispatch(setContextPanelTurn(msg.turn_id));
                    }
                  : undefined
              }
            />
          </div>
        )}
        {showContext && (
          <ContextPanel
            answerText={textContent}
            onClose={() => dispatch(setContextPanelTurn(null))}
          />
        )}
      </div>
    </div>
  );

  const renderAssumptionMessage = () => (
    <Accordion title="Assumptions" >
      <p>{assumptionText}</p>
    </Accordion>
  )

  const renderChartConfigMessage = () => (
    <div className="chat-block">
      <ChartBlock highchartsConfig={chartConfig || {}} themed />
    </div>
  );

  const renderTableMessage = () => (
    <div className="chat-block">
      <TableBlock columns={tableConfig?.columns || []} rows={tableConfig?.rows || []} />
    </div>
  );

  const renderReportMessage = () => (
    <div className="chat-block">
      <ReportCard
        report_title={report_title}
        report_status={report_status}
        report_status_message={report_status_message}
        onClick={() => {
          if (report_status === 'success') {
            const report = {
              id: report_id,
              title: report_title,
              data: report_data,
            };
            dispatch(addReport(report));
            dispatch(setActiveReport(report_id));
          }
        }}
      />
    </div>
  );

  const renderByType = () => {
    switch (message_type) {
      case "assumption":
        return renderAssumptionMessage();
      case "chart":
        return renderChartConfigMessage();
      case "table":
        return renderTableMessage();
      case "report":
        return renderReportMessage();
      default:
        return renderTextMessage();
    }
  };

  return (
    <li
      ref={messageRef}
      className={`msg-text ${senderType === "You" ? "align-right" : "align-left"}`}
    >
      {renderByType()}
    </li>
  );
};

export default Message;
